/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Button, Popover } from 'grab-ui';
import closeIcon from 'public/icons/delete-cross.svg';
import cx from 'classnames';
import useWindowScroll from './useWindowScroll';
import usePreventScroll from './usePreventScroll';
import useSuppressFlagStorage from './useSuppressFlagStorage';
import styles from './contextual-onboarding.less';

const onboardingCounterKey = onboardingID => `__onboarding-${onboardingID}__`;

const useOutsideClick = (ref, callback) => {
  const handleClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  });
};

const ContextualOnboarding = ({
  onboardingID,
  elementID,
  onClickID,
  onboardingVisual,
  onboardingVisualPlacement,
  triggerRules,
  delay = 0,
  maxOnboardingTimes = 2,
  highlightPaddingVertical,
  highlightPaddingHorizontal,
  className,
}) => {
  const [visible, setVisibility] = useState(false);
  const [highlightRect, setHighlightRect] = useState(null);
  const highlightRef = useRef();
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const yOffset = useWindowScroll();
  const { enableScroll, disableScroll } = usePreventScroll();
  const { checkSuppress, setSuppress } = useSuppressFlagStorage(onboardingID);

  const isTriggered = useMemo(
    () => {
      if (triggerRules.suppress === true) {
        if (!checkSuppress()) setSuppress(true);
        return false;
      }
      if (triggerRules.yOffset && yOffset < triggerRules.yOffset) return false;
      return true;
    },
    [yOffset, triggerRules.yOffset, triggerRules.suppress],
  );

  const shouldShow = useMemo(
    () => {
      if (!isTriggered) return false;

      const counter = window.localStorage.getItem(
        onboardingCounterKey(onboardingID),
      );

      if (!counter) {
        return true;
      }
      return +counter < maxOnboardingTimes;
    },
    [onboardingID, maxOnboardingTimes, isTriggered],
  );

  const onDismiss = useCallback(
    () => {
      setVisibility(false);
    },
    [setVisibility],
  );

  useOutsideClick(highlightRef, onDismiss);

  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onClickAgent = useCallback(
    () => {
      onDismiss();
      const element = document.getElementById(onClickID || elementID);
      if (element) {
        setTimeout(() => {
          element.click();
        }, 100);
      }
    },
    [onDismiss, elementID, onClickID],
  );

  useEffect(
    () => {
      if (!shouldShow) {
        return;
      }
      const element = document.getElementById(elementID);
      if (!element || !element.getBoundingClientRect) {
        return;
      }
      const elementRect = element.getBoundingClientRect();
      if (!elementRect) {
        return;
      }
      setHighlightRect({
        left: elementRect.left - highlightPaddingHorizontal,
        top: elementRect.top - highlightPaddingVertical,
        width: elementRect.width + 2 * highlightPaddingHorizontal,
        height: elementRect.height + 2 * highlightPaddingVertical,
      });
    },
    [screenSize],
  );

  useEffect(
    () => {
      if (!shouldShow) {
        return;
      }
      const timer = setTimeout(() => {
        if (checkSuppress()) {
          return;
        }

        const element = document.getElementById(elementID);
        if (!element || !element.getBoundingClientRect) {
          return;
        }
        const elementRect = element.getBoundingClientRect();
        if (!elementRect) {
          return;
        }
        setVisibility(true);
        setHighlightRect({
          left: elementRect.left - highlightPaddingHorizontal,
          top: elementRect.top - highlightPaddingVertical,
          width: elementRect.width + 2 * highlightPaddingHorizontal,
          height: elementRect.height + 2 * highlightPaddingVertical,
        });

        const counterKey = onboardingCounterKey(onboardingID);
        const counter = window.localStorage.getItem(counterKey);
        const newCounter = counter === null ? '1' : `${+counter + 1}`;
        window.localStorage.setItem(counterKey, newCounter);
      }, delay);
      return () => clearTimeout(timer);
    },
    [shouldShow, elementID, delay, setVisibility, setHighlightRect],
  );

  useEffect(
    () => {
      if (visible) {
        const styleTag = document.createElement('style');
        styleTag.setAttribute('type', 'text/css');
        styleTag.innerHTML = `#${elementID} { z-index: 10001; }`;
        const head = document.getElementsByTagName('head');
        if (head && head[0]) {
          head[0].append(styleTag);
        }

        disableScroll();

        return () => {
          if (styleTag) {
            styleTag.remove();
          }
          enableScroll();
        };
      }
    },
    [visible],
  );

  if (!visible) {
    return null;
  }

  return (
    <div>
      <div
        ref={highlightRef}
        style={highlightRect}
        className={styles.highlight}
      />
      {onboardingVisual && (
        <Popover
          visible
          overlayClassName={cx(styles.onboardingVisualPopover, className)}
          content={
            <div>
              <Button className={styles.close} onClick={onDismiss}>
                <img src={closeIcon} alt={'close'} />
              </Button>
              {onboardingVisual}
            </div>
          }
          placement={onboardingVisualPlacement}
        >
          <div
            style={highlightRect}
            className={styles.clickAgent}
            onClick={onClickAgent}
          />
        </Popover>
      )}
    </div>
  );
};

ContextualOnboarding.defaultProps = {
  onboardingVisual: undefined,
  onboardingVisualPlacement: 'left',
  delay: 500,
  maxOnboardingTimes: 1,
  triggerRules: {},
  highlightPaddingVertical: 10,
  highlightPaddingHorizontal: 10,
  className: undefined,
  onClickID: undefined,
};

ContextualOnboarding.propTypes = {
  onboardingID: PropTypes.string.isRequired,
  elementID: PropTypes.string.isRequired,
  onClickID: PropTypes.string,
  onboardingVisual: PropTypes.shape({}),
  onboardingVisualPlacement: PropTypes.string.isRequired,
  delay: PropTypes.number,
  maxOnboardingTimes: PropTypes.number,
  triggerRules: PropTypes.shape({}),
  highlightPaddingVertical: PropTypes.number,
  highlightPaddingHorizontal: PropTypes.number,
  className: PropTypes.string,
};

export { onboardingCounterKey };
export default ContextualOnboarding;
